import { useSuspenseQuery } from "@tanstack/react-query";
import { createRoute, type AnyRoute } from "@tanstack/react-router";

import { withRouteRetry } from "@/app/route-retry";
import { loadView, shareViewQuery, viewQuery } from "@/api/views";
import { brandHead, customerHead, NOINDEX_ROBOTS, withRobots } from "@/lib/head";
import QuotePage from "@/pages/app/quotes/[id]/page";
import QuotePrintPage from "@/pages/app/quotes/[id]/print/page";
import QuotesPage from "@/pages/app/quotes/page";
import LinkErrorPage from "@/pages/s/[token]/error";
import LinkNotFound from "@/pages/s/[token]/not-found";
import SharePage from "@/pages/s/[token]/page";

import { appRoute, APP_BOUNDARIES, sRoute } from "./w0b";

const RetryingLinkError = withRouteRetry(LinkErrorPage);

interface QuotesSearch {
  q?: string;
  status?: string;
  latest?: string;
  page?: string;
}

interface ShareSearch {
  t?: string;
}

interface QuoteSearch {
  tab?: string;
}

// 주소의 쿼리는 사람이 고칠 수 있다 — 글자·숫자만 문자열로 받고 나머지는 버린다(값 판정은 core 가 한다).
const textOf = (value: unknown): string | undefined =>
  typeof value === "string" ? value : typeof value === "number" ? String(value) : undefined;

const quotesQuery = (search: QuotesSearch) => viewQuery("quotes", search);
const quoteQuery = (id: string) => viewQuery("quote", { id });
const quotePrintQuery = (id: string) => viewQuery("quote-print", { id });
const customerQuoteQuery = (token: string) => shareViewQuery("s", token, "customer-quote");

const QuotesRouteComponent = () => {
  const search = quotesRoute.useSearch();
  const { data } = useSuspenseQuery(quotesQuery(search));
  return <QuotesPage data={data} />;
};

const quotesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/quotes",
  validateSearch: (search: Record<string, unknown>): QuotesSearch => ({
    q: textOf(search.q),
    status: textOf(search.status),
    latest: textOf(search.latest),
    page: textOf(search.page),
  }),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => loadView(context.queryClient, quotesQuery(deps)),
  head: () => brandHead("견적"),
  component: QuotesRouteComponent,
  ...APP_BOUNDARIES,
});

const QuoteRouteComponent = () => {
  const { id } = quoteRoute.useParams();
  const { tab } = quoteRoute.useSearch();
  const { data } = useSuspenseQuery(quoteQuery(id));
  return <QuotePage data={data} tab={tab} />;
};

const quoteRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/quotes/$id",
  // 탭은 화면만 가른다(손익 데이터는 같은 view 에 있다) — 읽기 키에 넣지 않는다.
  validateSearch: (search: Record<string, unknown>): QuoteSearch => ({ tab: textOf(search.tab) }),
  loader: ({ context, params }) => loadView(context.queryClient, quoteQuery(params.id)),
  // 제목은 읽어 둔 견적번호·버전 — 없으면 「견적」.
  head: ({ match, params }) => brandHead(match.context.queryClient.getQueryData(quoteQuery(params.id).queryKey)?.title ?? "견적"),
  component: QuoteRouteComponent,
  ...APP_BOUNDARIES,
});

const QuotePrintRouteComponent = () => {
  const { id } = quotePrintRoute.useParams();
  const { data } = useSuspenseQuery(quotePrintQuery(id));
  return <QuotePrintPage data={data} />;
};

const quotePrintRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/quotes/$id/print",
  loader: ({ context, params }) => loadView(context.queryClient, quotePrintQuery(params.id)),
  head: () => brandHead("견적서 인쇄"),
  component: QuotePrintRouteComponent,
  ...APP_BOUNDARIES,
});

const ShareRouteComponent = () => {
  const { token } = shareRoute.useParams();
  const { t } = shareRoute.useSearch();
  const { data } = useSuspenseQuery(customerQuoteQuery(token));
  return <SharePage data={data} t={t} token={token} />;
};

// 고객 링크 — 오류·없는 토큰은 고객용 문구로(내부 메시지를 보이지 않는다).
const shareRoute = createRoute({
  getParentRoute: () => sRoute,
  path: "/$token",
  validateSearch: (search: Record<string, unknown>): ShareSearch => ({ t: textOf(search.t) }),
  loader: ({ context, params }) => loadView(context.queryClient, customerQuoteQuery(params.token)),
  head: () => withRobots(customerHead("견적서"), NOINDEX_ROBOTS),
  component: ShareRouteComponent,
  errorComponent: RetryingLinkError,
  notFoundComponent: LinkNotFound,
});

// g1b 배치의 라우트 — getParentRoute 가 가리키는 부모 아래로 root.tsx 가 꽂는다.
export const routes: AnyRoute[] = [quotesRoute, quoteRoute, quotePrintRoute, shareRoute];
